import { FanforgeUserJwtClaims } from '@hotpot-arcade/fanforge-core/types/auth';
import { UserTier } from './types';

// Base event properties that are automatically enriched
export interface BaseEventProperties {
  timestamp: string;
  platform: 'web' | 'discord';
  user_id: string | null; // Nullable - anonymous users won't have this
  guild_id: string | null; // Nullable - not all events have guild context
  instance_id: string | null; // Null for web platform
  channel_id: string | null; // Nullable - not all events have channel context
  discord_id: string | null; // Nullable - only Discord auth has this
}

type MeowMeowGameModes = 'Normal' | 'DailyChallenge';

export type MessageContext = 'discord_menu_button' | 'discord_play_now_button';

// Event property definitions for each event type
type ActivityLaunchedProps = {
  guild_id?: string;
  channel_id?: string;
  game_id?: string;
  message_context?: MessageContext;
  message_source?: string;
};
type ActivityClosedProps = {};

// FTUE events
type FtueStartedProps = { ftue_id: string };
type FtueCompletedProps = { ftue_id: string };

// Modal/Popup events
type DailyPopupShownProps = {};
type DailyPopupClosedProps = {};

// Generic game events
type GameOpenedProps = { game_name: string };
type GameClosedProps = { game_name: string };
type GameRoundStartedProps = {
  game_name: string;
  game_mode?: string; // For games with multiple modes (like meowmeow)
  // Game-specific properties as needed
  userid?: string; // For mafia client
  setup_type?: string; // Mafia only: host's setup mode (beginner/standard/auto/custom)
  roles?: string[]; // Mafia only: role composition in play, e.g. ['mafia','detective','townsfolk','townsfolk']
};
type GameRoundCompletedProps = {
  game_name: string;
  game_mode?: string;
  // Game-specific properties as needed
  score_earned?: number; // For games with scores
  userid?: string; // For mafia client
};

// Specific game events
type GameMafiaStartedServerProps = { player_count: number; role_config: string };
type GameMafiaStartedClientProps = { userid: string };
type GameMeowmeowStartedProps = { game_mode: MeowMeowGameModes };
type GameBubboStartedProps = {};
type GameJuiceJamStartedProps = {};

type GameMafiaCompletedServerProps = { player_count: number; faction_winner: string; role_config: string };

type GameBubboCompletedProps = { score_earned: number; duration_seconds?: number };
type GameJuiceJamCompletedProps = { score_earned: number; duration_seconds?: number };

// Reward events
type RewardGrantedProps = { points_granted: number; reason: string };

// Meme/Building events
type MemeAddedProps = { meme_id: string; points_amount: number };
type MemeContributionProps = { meme_id: string; points_amount: number };
type MemeInteractionProps = { meme_id: string };

// Social events
type ContributionLikedProps = { contributor_user_id: string };
type ContributionUnlikedProps = { contributor_user_id: string };

// Recognition events
type RecognitionDoseProps = {
  discordId: string;
  guildId: string;
  recognitionContext: string;
  secondsViewed: number;
  recognizedUserTier: UserTier;
};

// Nudges/Notification events
type DailyCombinedReportSentProps = {
  guild_id: string;
  channel_id: string;
};

// New user joins guild
type NewUserJoinGuildProps = {
  discord_id: string;
  guild_id: string;
};

// Welcome message for new users
type WelcomeMessageProps = {
  guide_discord_id: string;
  guild_id: string;
  newcomer_discord_id: string;
  points: number;
  message: string;
};

// Reactions to welcome messages
type WelcomeReactionProps = {
  discord_id: string;
  guild_id: string;
  message_id: string;
  points: number;
};

type NewUsersWeeklyReportProps = {
  guild_id: string;
  new_members: number;
  total_welcomes: number;
  week_range: string;
};

// User removed from guild
type UserRemovedFromGuildProps = {
  discord_id: string;
  guild_id: string;
};

// Specific game chat events
type GameMafiaChatSentProps = { messageType: string; content: string };

// Game interaction events
type GameCaketowerCakeAddedProps = { caketowerHeight: number };

export type DiscordCommandInvocationStatus = 'success' | 'validation_error' | 'error';
export type DiscordCommandInvocationSource = 'slash' | 'component' | 'modal';

export type DiscordCommandInvokedProps = {
  command_name: string;
  status: DiscordCommandInvocationStatus;
  invocation_source: DiscordCommandInvocationSource;
  guild_id?: string;
  channel_id?: string;
  error_message?: string;
};

export type ChallengeLogReactionProps = {
  guild_id: string;
  channel_id: string;
  message_id: string;
  challenge_id: string;
};

export type MeowmeowShopEventProps = {
  guild_id?: string;
  item_id?: string;
  price?: string;
};

export type MeowmeowClickEventProps = {
  source: string;
};

export type CoinShopPurchaseButtonClickedProps = {
  guild_id?: string;
  sku: {
    id: string;
    name: string;
  };
};

// Discriminated union of all analytics events
export type AnalyticsEvent =
  // Activity events
  | { name: 'activity_launched'; props: ActivityLaunchedProps }
  | { name: 'activity_closed'; props: ActivityClosedProps }

  // FTUE events
  | { name: 'ftue_started'; props: FtueStartedProps }
  | { name: 'ftue_completed'; props: FtueCompletedProps }

  // Modal/Popup events
  | { name: 'daily_popup_shown'; props: DailyPopupShownProps }
  | { name: 'daily_popup_closed'; props: DailyPopupClosedProps }

  // Generic game events
  | { name: 'game_opened'; props: GameOpenedProps }
  | { name: 'game_closed'; props: GameClosedProps }
  | { name: 'game_round_started'; props: GameRoundStartedProps }
  | { name: 'game_round_completed'; props: GameRoundCompletedProps }

  // Specific game start events
  | { name: 'game_mafia_server_started'; props: GameMafiaStartedServerProps }
  | { name: 'game_bubbo_started'; props: GameBubboStartedProps }
  | { name: 'game_mafia_client_started'; props: GameMafiaStartedClientProps }
  | { name: 'game_juicejam_started'; props: GameJuiceJamStartedProps }

  // Specific game completion events
  | { name: 'game_mafia_server_completed'; props: GameMafiaCompletedServerProps }
  | { name: 'game_bubbo_completed'; props: GameBubboCompletedProps }
  | { name: 'game_juicejam_completed'; props: GameJuiceJamCompletedProps }

  // Reward events
  | { name: 'reward_granted'; props: RewardGrantedProps }

  // Meme/Building events
  | { name: 'meme_added'; props: MemeAddedProps }
  | { name: 'meme_contribution'; props: MemeContributionProps }
  | { name: 'meme_interaction'; props: MemeInteractionProps }

  // Social events
  | { name: 'contribution_liked'; props: ContributionLikedProps }
  | { name: 'contribution_unliked'; props: ContributionUnlikedProps }

  // Recognition events
  | { name: 'recognition_dose'; props: RecognitionDoseProps }

  // Nudges/Notification events
  | { name: 'daily_combined_report_sent'; props: DailyCombinedReportSentProps }

  // Community guide events
  | { name: 'new_user_join_guild'; props: NewUserJoinGuildProps }
  | { name: 'new_user_welcomed'; props: WelcomeMessageProps }
  | { name: 'new_user_message_reaction'; props: WelcomeReactionProps }
  | { name: 'new_users_weekly_report_sent'; props: NewUsersWeeklyReportProps }
  | { name: 'user_removed_from_guild'; props: UserRemovedFromGuildProps }

  // Discord slash command events
  | { name: 'discord_command_invoked'; props: DiscordCommandInvokedProps }

  // Challenge events
  | { name: 'challenge_log_reaction'; props: ChallengeLogReactionProps }

  // Specific game chat events
  | { name: 'game_mafia_chat_sent'; props: GameMafiaChatSentProps }

  // Game interaction events
  | { name: 'game_caketower_cake_added'; props: GameCaketowerCakeAddedProps }

  // Meowmeow shop events events
  | { name: 'meowmeow_skin_purchase_start'; props: MeowmeowShopEventProps }
  | { name: 'meowmeow_skin_purchase_confirm'; props: MeowmeowShopEventProps }
  | { name: 'meowmeow_skin_purchase_cancel'; props: MeowmeowShopEventProps }
  | { name: 'meowmeow_skin_equip'; props: MeowmeowShopEventProps & MeowmeowClickEventProps }
  | { name: 'meowmeow_skin_equip_dismiss'; props: MeowmeowShopEventProps }
  | { name: 'meowmeow_purchase_coins'; props: MeowmeowShopEventProps }
  | { name: 'meowmeow_shop_cta_click'; props: MeowmeowClickEventProps }

  // Coin shop events
  | { name: 'coin_shop_purchase_button_clicked'; props: CoinShopPurchaseButtonClickedProps }
  | { name: 'coin_shop_purchase_completed'; props: CoinShopPurchaseButtonClickedProps };

// Helper type to extract event names
export type EventName = AnalyticsEvent['name'];

// Helper type to extract props for a specific event
export type EventProps<T extends EventName> = Extract<AnalyticsEvent, { name: T }>['props'];

// Function to enrich event properties for anonymous users
export function enrichAnonymousProperties<T extends AnalyticsEvent>(event: T): T['props'] & BaseEventProperties {
  return {
    timestamp: new Date().toISOString(),
    platform: 'web',
    user_id: null,
    guild_id: null,
    instance_id: null,
    channel_id: null,
    discord_id: null,
    ...event.props,
  };
}

// Function to enrich event properties for authenticated users
export function enrichAuthenticatedProperties<T extends AnalyticsEvent>(
  event: T,
  jwtPayload: FanforgeUserJwtClaims | null
): T['props'] & BaseEventProperties {
  const isDiscord = jwtPayload?.amr === 'discord';

  return {
    timestamp: new Date().toISOString(),
    platform: jwtPayload?.amr ?? 'web',
    user_id: jwtPayload?.sub ?? '',
    guild_id: isDiscord ? (jwtPayload.guildId ?? null) : null,
    instance_id: isDiscord ? (jwtPayload.instanceId ?? null) : null,
    channel_id: isDiscord ? (jwtPayload.channelId ?? null) : null,
    discord_id: isDiscord ? (jwtPayload.discordId ?? null) : null,
    ...event.props,
  };
}
